import {
	AggregateType,
	DataType,
	DustDataType,
	OverpressureDataType,
	SoundDataType,
	SoundPonderation,
	SpectrumType,
	VibrationAccelerationDataType,
	VibrationAxis,
	VibrationSubType,
	VibrationVelocityDataType,
	WeatherDataType,
} from "@soft-db/data-formatter";
import {
	MetricDataType,
	RateLimiterMode,
	SeverityLevel,
	ThresholdComparison,
	ValidityComparison,
} from "@soft-db/monitoring-api-client";

export const useDataCatalog = () => {
	const { t } = useI18n();

	const QueryType: Record<DataType | "", string> = {
		sound: t("dataType.sound"),
		vibration: t("dataType.vibration"),
		weather: t("dataType.weather"),
		overpressure: t("dataType.overpressure"),
		dust: t("dataType.dust"),
		metric: t("dataType.metric"),
		"": "",
	};

	const WeatherDataType: Record<Exclude<WeatherDataType, "wind">, string> = {
		humidity: t("data.type.weather.humidity"),
		pressure: t("data.type.weather.pressure"),
		rain_rate: t("data.type.weather.rain_rate"),
		temperature: t("data.type.weather.temperature"),
		wind_direction: t("data.type.weather.wind_direction"),
		wind_speed: t("data.type.weather.wind_speed"),
	};

	const DustDataType: Record<Exclude<DustDataType, "flow_rate">, string> = {
		concentration: t("data.type.dust.concentration"),
	};

	const EventType: Record<string, string> = {
		sound: t("dataType.sound"),
		vibration: t("dataType.vibration"),
		overpressure: t("dataType.overpressure"),
		"": "",
	};

	const VibrationSubType: Record<VibrationSubType, string> = {
		acceleration: t("dataType.acceleration"),
		velocity: t("dataType.velocity"),
	};

	const Sound: Record<SoundDataType, string> = {
		leq: t("data.type.sound.leq"),
		lpk: t("data.type.sound.lpk"),
		lftm5: t("data.type.sound.lftm5"),
		l_min: t("data.type.sound.lmin"),
		l_max: t("data.type.sound.lmax"),
		ln: t("data.type.sound.ln"),
	};

	const SoundPonderation: Record<SoundPonderation, string> = {
		a: t("data.type.sound.ponderation.a"),
		c: t("data.type.sound.ponderation.c"),
		z: t("data.type.sound.ponderation.z"),
		g: t("data.type.sound.ponderation.g"),
	};

	const VibrationAcceleration: Record<VibrationAccelerationDataType, string> = {
		rms: t("data.type.vibration.acceleration.rms"),
		rms_max_hold: t("data.type.vibration.acceleration.rmsMaxHold"),
		rms_wm: t("data.type.vibration.acceleration.rmsWm"),
		rms_wm_max_hold: t("data.type.vibration.acceleration.rmsWmMaxHold"),
		peak: t("data.type.vibration.acceleration.peak"),
		peak_wm: t("data.type.vibration.acceleration.peakWm"),

		// Legacy
		max_hold: t("data.type.vibration.max_hold"),
	};

	const VibrationVelocity: Record<VibrationVelocityDataType, string> = {
		rms: t("data.type.vibration.velocity.rms"),
		rms_max_hold: t("data.type.vibration.velocity.rmsMaxHold"),
		peak: t("data.type.vibration.velocity.peak"),
		kbf_max: t("data.type.vibration.velocity.kbfMax"),
		kbftm: t("data.type.vibration.velocity.kbftm"),
		vb1_max: t("data.type.vibration.velocity.vb1Max"),
		vb2_max: t("data.type.vibration.velocity.vb2Max"),
		vb3_max: t("data.type.vibration.velocity.vb3Max"),

		// Legacy
		max_hold: t("data.type.vibration.max_hold"),
	};

	const VibrationAxis: Record<VibrationAxis, string> = {
		x: t("data.type.vibration.axis.x"),
		y: t("data.type.vibration.axis.y"),
		z: t("data.type.vibration.axis.z"),
		v_sum: t("data.type.vibration.axis.vSum"),
	};

	const Spectrum: Record<Exclude<SpectrumType, "24"> | "", string> = {
		"1": t("data.type.spectrum.1"),
		"3": t("data.type.spectrum.3"),
		fft: t("data.type.spectrum.fft"),
		"": "",
	};

	const Metric: Record<MetricDataType, string> = {
		battery_soc: t("data.type.metric.battery_percent"),
		idle: t("data.type.metric.idle"),
		latency: t("data.type.metric.latency"),
		measuring: t("data.type.metric.measuring"),
		offline: t("data.type.metric.offline"),
		battery_percent: t("data.type.metric.battery_percent"),
		battery_voltage: t("data.type.metric.battery_voltage"),
		longitude: t("common.longitude"),
		latitude: t("common.latitude"),
		input_voltage: t("integrity-settings.powerSupply"),
		data_usage_record: t("integrity-settings.dataUsage") + " " + t("archive.record"),
		data_usage_event: t("integrity-settings.dataUsage") + " " + t("dashboard.events"),
	};

	const WeatherAggregates: Record<AggregateType, string> = {
		avg: t("data.type.weather.aggregate.avg"),
		min: t("data.type.weather.aggregate.min"),
		max: t("data.type.weather.aggregate.max"),
	};

	const WeatherAggregatesShort: Record<AggregateType, string> = {
		avg: t("dashboard.avg"),
		min: t("dashboard.min"),
		max: t("dashboard.max"),
	};

	const Overpressure: Record<OverpressureDataType, string> = {
		peak: t("data.type.vibration.acceleration.peak"),
	};

	const Severity: Record<Exclude<SeverityLevel, "none">, string> = {
		error: t("common.error"),
		warning: t("common.warning"),
	};

	const RateLimiter: Record<RateLimiterMode, string> = {
		ignore: t("alert-settings.rateLimiterModes.ignore"),
		accumulate: t("alert-settings.rateLimiterModes.accumulate"),
	};

	const Comparison: Record<ThresholdComparison, string> = {
		gt: t("common.greaterThan"),
		lt: t("common.lessThan"),
	};

	const ExtendedComparison: Record<ValidityComparison, string> = {
		gt: t("common.greaterThan"),
		lt: t("common.lessThan"),
		bt: t("common.between"),
		nbt: t("common.outside"),
	};

	type MenuEntry = {
		value: string;
		name: string;
	};
	const generateMenuEntries = (data: Record<string, string>): MenuEntry[] => {
		return Object.entries(data)
			.map((entry) => ({
				value: entry[0],
				name: entry[1],
			}))
			.filter((entry) => !!entry.value);
	};

	return {
		QueryType,
		EventType,
		VibrationSubType,
		Sound,
		SoundPonderation,
		VibrationAcceleration,
		VibrationVelocity,
		VibrationAxis,
		Spectrum,
		Metric,
		WeatherAggregates,
		WeatherAggregatesShort,
		WeatherDataType,
		DustDataType,
		Overpressure,
		Severity,
		RateLimiter,
		Comparison,
		ExtendedComparison,
		generateMenuEntries,
	};
};
